/**
 * tienda-comun.js — Lo que comparten los 4 bloques de tienda (`ofertas`, `novedades`, `top`,
 * `habitual`): su configuración, la regla que decide qué enseña la sección y el HTML de cada
 * tarjeta.
 *
 * Tres consumidores, una sola verdad:
 *   · blocks/tienda.js        → el render (editor y `compileSiteHtml`): la sección, su chrome
 *                                del editor y los huecos que se rellenan con datos.
 *   · core/tienda-runtime.js  → pide los datos (al editor, a su API; en lo publicado, a la
 *                                TIENDA) y pinta las tarjetas con las funciones de aquí.
 *   · editor/tienda-panel.js  → las pestañas de producto del panel «Tienda».
 *
 * Módulo PURO: sin DOM ni `window`, para que lo ejerciten los tests en node (por eso va en ES2019,
 * sin `?.` ni `??`: el node de los tests es el 12). Solo importa el escape de HTML.
 *
 * Por qué no hay datos guardados en el bloque: precios, ofertas y compras cambian por visitante y
 * por día. El bloque guarda solo CÓMO se ve (`source`, `visibility`, `fallback`, `prices`, `cart`,
 * `variant` y sus textos) y los datos llegan siempre en vivo. Contrato completo:
 * CLAUDE_RESUMEN_PLANES/2026-10-06_tienda-bloques/PLAN.md.
 */
import { escapeHtml } from '../utils/dom.js';

/** Configuración de cada tipo. `t*` son claves de i18n.js (etiquetas de la web publicada). */
export const TIENDA_TIPOS = {
    ofertas: {
        counts: [3, 6, 12], count: 6, orders: ['priority', 'ending'], order: 'priority',
        visibility: 'sesion', variant: 'v1', variants: ['v1', 'v2', 'v3'], producto: false,
        tTitle: 'shop_offers_title', tLoginTitle: 'shop_login_title_offers', tLoginText: 'shop_login_text_offers',
    },
    novedades: {
        counts: [4, 8, 12], count: 8, orders: [], order: '',
        visibility: 'sesion', variant: 'v1', variants: ['v1', 'v2'], producto: true, tTag: 'shop_tag_new',
        tTitle: 'shop_new_title', tLoginTitle: 'shop_login_title_new', tLoginText: 'shop_login_text_new',
    },
    top: {
        counts: [4, 8, 12], count: 8, orders: [], order: '',
        visibility: 'todos', variant: 'v1', variants: ['v1', 'v2'], producto: true, tTag: 'shop_tag_top',
        tTitle: 'shop_top_title', tLoginTitle: 'shop_login_title_top', tLoginText: 'shop_login_text_top',
    },
    // «Lo que sueles comprar» = «Volver a comprar» de la tienda: necesita el historial del
    // cliente, que solo existe con sesión → `soloSesion` (nunca «Todo el mundo»).
    habitual: {
        counts: [4, 8, 12], count: 8, orders: [], order: '',
        visibility: 'sesion', variant: 'v2', variants: ['v1', 'v2'], producto: true, soloSesion: true,
        tTitle: 'shop_usual_title', tLoginTitle: 'shop_login_title_usual', tLoginText: 'shop_login_text_usual',
    },
};

export const TIENDA_KINDS = Object.keys(TIENDA_TIPOS);

// URL pública de la TIENDA (otro Site, otro host): el render la hornea en `data-tienda` para que el
// sitio publicado sepa a quién pedir los datos y adónde mandar «Iniciar sesión». Es ESTADO DE
// RENDER, como el destino global de botones (utils/links.js): la fija el editor con lo que contesta
// `api/escaparate/estado/` (core/tienda-runtime.js) y no se guarda en el bloque, así que si la
// tienda cambia de dominio basta con volver a publicar.
let _shop = '';

/** Fija la URL de la tienda ('' = sin tienda). Solo http(s), sin barra final. */
export function setTiendaShop(url) {
    const v = String(url == null ? '' : url).trim().replace(/\/+$/, '');
    _shop = /^https?:\/\/[^\s"'<>]+$/i.test(v) ? v : '';
}

/** URL de la tienda que se hornea en las secciones ('' si no se conoce). */
export function getTiendaShop() {
    return _shop;
}

/**
 * Por qué una pestaña o una sección no tiene datos (`reason` de `api/escaparate/`). Texto del
 * EDITOR: lo enseñan el aviso ámbar del panel y el marcador `.editor-only` de la sección. Los
 * nombres entre comillas son los de la configuración de la tienda online, tal cual.
 */
export const TIENDA_MOTIVOS = {
    sinTienda: 'Ofertas, novedades, más vendidos y lo que suele comprar cada cliente salen de tu tienda online. Actívala para usar estas secciones.',
    sinOfertas: 'Activa «Mostrar lista de "Ofertas"» en la configuración de la tienda online y vuelve aquí.',
    sinNovedades: 'Activa «Mostrar lista de "Nuevos productos"» en la configuración de la tienda online y vuelve aquí.',
    sinTop: 'Activa «Mostrar lista de "Los más vendidos"» en la configuración de la tienda online y vuelve aquí.',
    error: 'No se han podido traer los datos de la tienda. Prueba a recargar el editor.',
};

/** Título del aviso ámbar de cada motivo (panel «Tienda»). */
export const TIENDA_MOTIVOS_TITULO = {
    sinTienda: 'No tienes la tienda online activa',
    sinOfertas: 'Las ofertas están apagadas en tu tienda',
    sinNovedades: 'Las novedades están apagadas en tu tienda',
    sinTop: 'Los más vendidos están apagados en tu tienda',
    error: 'No se ha podido consultar la tienda',
};

/** ¿Es `type` uno de los bloques de tienda de producto/oferta? */
export function esBloqueTienda(type) {
    return Object.prototype.hasOwnProperty.call(TIENDA_TIPOS, type);
}

/**
 * Las opciones del bloque, con sus valores por defecto y saneadas: lo que el render, la barra
 * del editor y el runtime leen. Un valor fuera de lo permitido cae al defecto del tipo.
 */
export function opcionesTienda(kind, props) {
    const cfg = TIENDA_TIPOS[kind] || TIENDA_TIPOS.novedades;
    const p = props || {};
    const src = (p.source && typeof p.source === 'object') ? p.source : {};
    const n = parseInt(src.count, 10);
    const count = cfg.counts.indexOf(n) !== -1 ? n : cfg.count;
    const order = cfg.orders.indexOf(src.order) !== -1 ? src.order : cfg.order;
    const visibility = cfg.soloSesion ? 'sesion' : (p.visibility === 'todos' || p.visibility === 'sesion' ? p.visibility : cfg.visibility);
    const fallback = p.fallback === 'aviso' ? 'aviso' : 'ocultar';
    const variant = cfg.variants.indexOf(p.variant) !== -1 ? p.variant : cfg.variant;
    const prices = cfg.producto && p.prices === 'hide' ? 'hide' : 'show';
    const cart = cfg.producto && p.cart === 'cesta' ? 'cesta' : 'ver';
    return { kind: kind, count: count, order: order, visibility: visibility, fallback: fallback,
             variant: variant, prices: prices, cart: cart };
}

/**
 * Cambia UNA opción del bloque desde la barra del editor. Devuelve true si cambió algo.
 * La misma validación que `opcionesTienda`: lo que la barra no ofrece (precio en ofertas, «Todo el
 * mundo» en habitual, banner fuera de ofertas) no entra aunque llegue un clic forzado.
 */
export function aplicarOpcionTienda(block, campo, valor) {
    if (!block || !esBloqueTienda(block.type)) return false;
    const cfg = TIENDA_TIPOS[block.type];
    const props = block.props || (block.props = {});
    const antes = JSON.stringify(opcionesTienda(block.type, props));
    if (campo === 'visibility') {
        if (valor !== 'todos' && valor !== 'sesion') return false;
        if (cfg.soloSesion && valor === 'todos') return false;
        props.visibility = valor;
    } else if (campo === 'fallback') {
        if (valor !== 'ocultar' && valor !== 'aviso') return false;
        props.fallback = valor;
    } else if (campo === 'prices' || campo === 'cart') {
        if (!cfg.producto) return false;
        if (campo === 'prices' && valor !== 'show' && valor !== 'hide') return false;
        if (campo === 'cart' && valor !== 'ver' && valor !== 'cesta') return false;
        props[campo] = valor;
    } else if (campo === 'variant') {
        if (cfg.variants.indexOf(valor) === -1) return false;
        props.variant = valor;
    } else {
        return false;
    }
    return JSON.stringify(opcionesTienda(block.type, props)) !== antes;
}

/**
 * Qué enseña la sección dada la respuesta del servidor. `resp` null = la petición falló.
 *   'items' → tarjetas · 'vacio' → no hay nada que enseñar · 'gate' → aviso para entrar
 *   · 'oculto' → la sección no aparece.
 * Una tienda PRIVADA trata al anónimo como «Solo con sesión», con el `fallback` del bloque.
 */
export function decidirEstado(opts, resp) {
    if (!resp || resp.ok === false || resp.off) return 'oculto';
    const pideSesion = opts.visibility === 'sesion' || !!resp.private;
    if (pideSesion && !resp.logged) return opts.fallback === 'aviso' ? 'gate' : 'oculto';
    return (Array.isArray(resp.items) && resp.items.length) ? 'items' : 'vacio';
}

// ── Formatos (idioma de la página) ────────────────────────────────────────────────────

const _MENOS = '−';   // «−20%»: signo menos tipográfico, como en el diseño

/** «8,95 €» en el idioma de la página. `price` llega como '8.95' (punto decimal) o null. */
export function formatoPrecio(price, lang) {
    const n = parseFloat(price);
    if (price == null || price === '' || isNaN(n)) return '';
    try {
        return new Intl.NumberFormat(lang || 'es', { style: 'currency', currency: 'EUR' }).format(n);
    } catch (e) {
        return n.toFixed(2).replace('.', ',') + ' €';
    }
}

/** «31 oct» en el idioma de la página. `iso` = 'AAAA-MM-DD'. '' si no es una fecha. */
export function formatoFecha(iso, lang) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    try {
        return new Intl.DateTimeFormat(lang || 'es', { day: 'numeric', month: 'short' }).format(d).replace(/\.$/, '');
    } catch (e) {
        return m[3] + '/' + m[2];
    }
}

/** «−20%» / «3x2» / '' (sin descuento, p. ej. una oferta solo de regalo). */
export function descuentoDe(item) {
    if (item && item.modular_text) return String(item.modular_text);
    const d = item ? parseFloat(item.discount) : 0;
    if (!d || isNaN(d) || d <= 0) return '';
    return _MENOS + String(item.discount).replace('.', ',') + '%';
}

/** ¿La oferta tiene más de un tramo? Entonces el descuento es «hasta». */
export function esHasta(item) {
    return !!item && !item.modular_text && Number(item.tiers) >= 2;
}

// ── HTML de las tarjetas ──────────────────────────────────────────────────────────────

// Iconos que pintan las TARJETAS (estilo Lucide, mismos trazos que utils/icons-library.js). Van
// aquí y no importados de icons-library porque este módulo entra en el bundle del sitio PUBLICADO
// (lo usa el runtime) y la librería entera son ~40 KB de SVG para dos iconos.
const ICONO = {
    regalo: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/></svg>',
    cesta: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>',
};

// Solo URLs http(s) absolutas: los ítems los compone el servidor de la tienda con su host. Un
// valor que no lo sea (vacío, `javascript:`…) no se pinta como enlace ni como imagen.
const _HTTP = /^https?:\/\//i;
const href = (u) => (_HTTP.test(String(u || '')) ? escapeHtml(String(u)) : '');

const _lbl = (labels, k) => escapeHtml((labels && labels[k]) || '');
const _sust = (texto, d) => String(texto || '').replace('{d}', d);

function _media(item, clase, extra) {
    const src = href(item.image);
    const img = src
        ? '<img src="' + src + '" alt="" loading="lazy" decoding="async">'
        : '<span class="ts-ph" aria-hidden="true"></span>';
    return '<div class="ts-media ' + clase + '">' + img + (extra || '') + '</div>';
}

/** Tarjeta de oferta (cuadrícula y carrusel). Toda la tarjeta enlaza a sus productos. */
export function tarjetaOferta(item, ctx) {
    const labels = ctx.labels || {};
    const dto = descuentoDe(item);
    const badges =
        (dto ? '<span class="ts-badge ts-badge-dto">' + (esHasta(item) ? _lbl(labels, 'upto') + ' ' : '') + escapeHtml(dto) + '</span>' : '') +
        (item.gift ? '<span class="ts-badge ts-badge-regalo">' + ICONO.regalo + _lbl(labels, 'gift') + '</span>' : '');
    const fecha = formatoFecha(item.end_date, ctx.lang);
    const url = href(item.url);
    const cuerpo =
        _media(item, 'ts-media-43', badges) +
        '<div class="ts-body">' +
            '<h3 class="ts-name">' + escapeHtml(item.title) + '</h3>' +
            (fecha ? '<p class="ts-meta">' + escapeHtml(_sust(labels.valid_until, fecha)) + '</p>' : '') +
            '<span class="ts-more">' + _lbl(labels, 'see_products') + ' <span aria-hidden="true">→</span></span>' +
        '</div>';
    return url
        ? '<a class="ts-card ts-card-oferta" href="' + url + '">' + cuerpo + '</a>'
        : '<div class="ts-card ts-card-oferta">' + cuerpo + '</div>';
}

/**
 * Tarjeta de producto (novedades, más vendidos, sueles comprar). El enlace a la ficha cubre la
 * tarjeta (`.ts-card-link`, estirado por CSS) y la acción va FUERA de él, como hermana: un botón
 * dentro de un <a> no es HTML válido (el prototipo lo hacía así y el handoff pide no copiarlo).
 */
export function tarjetaProducto(item, ctx) {
    const labels = ctx.labels || {};
    const url = href(item.url);
    const tag = ctx.tag ? '<span class="ts-tag">' + escapeHtml(ctx.tag) + '</span>' : '';
    const precio = ctx.prices === 'hide' ? '' : formatoPrecio(item.price, ctx.lang);
    const cesta = ctx.cart === 'cesta' ? href(item.cart_url) : '';
    const accion = cesta
        ? '<a class="ts-add" href="' + cesta + '" rel="nofollow">' + ICONO.cesta + '<span>' + _lbl(labels, 'add') + '</span></a>'
        : (url ? '<span class="ts-ver">' + _lbl(labels, 'view') + ' <span aria-hidden="true">→</span></span>' : '');
    const cabeza = _media(item, 'ts-media-11', tag) +
        '<div class="ts-body">' +
            (item.brand ? '<p class="ts-brand">' + escapeHtml(item.brand) + '</p>' : '') +
            '<h3 class="ts-name">' + escapeHtml(item.name) + '</h3>' +
        '</div>';
    return '<article class="ts-card ts-card-prod">' +
        (url ? '<a class="ts-card-link" href="' + url + '">' + cabeza + '</a>' : '<div class="ts-card-link">' + cabeza + '</div>') +
        '<div class="ts-foot">' +
            (precio ? '<span class="ts-price">' + escapeHtml(precio) + '</span>' : '<span></span>') +
            accion +
        '</div>' +
    '</article>';
}

/** Oferta principal del banner (v3): el cuerpo de texto. La foto va en su propio hueco. */
export function bannerCuerpo(item, ctx) {
    const labels = ctx.labels || {};
    const dto = descuentoDe(item);
    const fecha = formatoFecha(item.end_date, ctx.lang);
    const url = href(item.url);
    return (dto
            ? '<p class="ts-bdto">' + (esHasta(item) ? '<span class="ts-bhasta">' + _lbl(labels, 'upto') + '</span>' : '') +
              '<span class="ts-bpct">' + escapeHtml(dto) + '</span></p>'
            : '') +
        '<h3 class="ts-btitle">' + escapeHtml(item.title) + '</h3>' +
        ((fecha || item.gift)
            ? '<p class="ts-bmeta">' + (fecha ? '<span>' + escapeHtml(_sust(labels.valid_until, fecha)) + '</span>' : '') +
              (item.gift ? '<span class="ts-bregalo">' + ICONO.regalo + _lbl(labels, 'with_gift') + '</span>' : '') + '</p>'
            : '') +
        (url ? '<a class="ts-bbtn" href="' + url + '">' + _lbl(labels, 'see_products') + ' <span aria-hidden="true">→</span></a>' : '');
}

/** Foto de la oferta principal del banner. */
export function bannerFoto(item) {
    const src = href(item.image);
    return src ? '<img src="' + src + '" alt="" loading="lazy" decoding="async">' : '<span class="ts-ph" aria-hidden="true"></span>';
}

/** Ofertas secundarias del banner (hasta 3): fila compacta con el descuento a la derecha. */
export function bannerResto(items, ctx) {
    const labels = ctx.labels || {};
    return items.slice(0, 3).map(function (item) {
        const fecha = formatoFecha(item.end_date, ctx.lang);
        const url = href(item.url);
        const dentro =
            '<span class="ts-rtext"><span class="ts-rtitle">' + escapeHtml(item.title) + '</span>' +
            (fecha ? '<span class="ts-rmeta">' + escapeHtml(_sust(labels.until, fecha)) + '</span>' : '') + '</span>' +
            '<span class="ts-rdto">' + escapeHtml(descuentoDe(item)) + '</span>';
        return url ? '<a class="ts-card ts-rcard" href="' + url + '">' + dentro + '</a>'
                   : '<div class="ts-card ts-rcard">' + dentro + '</div>';
    }).join('');
}

/** Tarjetas de relleno mientras llegan los datos (mismo tamaño que las de verdad). */
export function esqueletos(kind, n) {
    const oferta = kind === 'ofertas';
    let html = '';
    for (let i = 0; i < n; i++) {
        html += '<div class="ts-card ts-skel" aria-hidden="true">' +
            '<div class="ts-media ' + (oferta ? 'ts-media-43' : 'ts-media-11') + '"></div>' +
            '<div class="ts-body"><span class="ts-skel-line"></span><span class="ts-skel-line ts-skel-short"></span></div>' +
        '</div>';
    }
    return html;
}
